// Overview section component for DealerDashboard
import React from 'react';
import { Card, Row, Col, Typography, Tooltip as AntdTooltip } from 'antd';
import { CarOutlined, RiseOutlined, UserOutlined, DollarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Top5Component from './Top5Component';
import TestDriveComponent from './TestDriveComponent';
import HotCarsComponent from './HotCarsComponent';

const { Title, Text } = Typography;

const OverviewSection = ({ 
  leftSwipesData, 
  rightSwipesData, 
  pieData, 
  handlePieClick,
  onActiveMembersClick,
  activeMembersCount,
  totalOpportunityMillions,
  dealerName
}) => {
  // Calculate total opportunity amount from right swipes data
  const calculateTotalOpportunityAmount = () => {
    if (!rightSwipesData || rightSwipesData.length === 0) return 0;
    
    const total = rightSwipesData.reduce((sum, item) => {
      // Try to parse the otdAmount or msrp field
      let amount = 0;
      if (item.otdAmount && item.otdAmount !== 'N/A') {
        amount = parseFloat(item.otdAmount.replace(/[$,]/g, ''));
      } else if (item.msrp) {
        amount = parseFloat(item.msrp.replace(/[$,]/g, ''));
      }
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    
    // Format as millions with one decimal
    return (total / 1000000).toFixed(1);
  };

  const totalOpportunity = (typeof totalOpportunityMillions === 'number' && !Number.isNaN(totalOpportunityMillions))
    ? totalOpportunityMillions.toFixed(1)
    : calculateTotalOpportunityAmount();

  return (
    <div className="dealer-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="dealership-info">
            <div className="dealership-icon">
              <CarOutlined />
            </div>
            <div>
              <Title level={1} style={{ margin: 0, fontSize: '32px', fontWeight: '700', color: '#262626' }}>
                {dealerName || 'Dealer Dashboard'}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px', color: '#8c8c8c', marginTop: '4px' }}>
                Dealer Rooftop Dashboard
              </Text>
            </div>
          </div>
        </div>
        <div className="header-right">
          <Card className="live-data-card">
            <div className="live-indicator">
              <div className="live-dot"></div>
              <Text strong style={{ fontSize: '14px', color: '#262626' }}>Live Data</Text>
            </div>
            <Text strong style={{ fontSize: '18px', color: '#262626' }}>
              {new Date().toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' })}
            </Text>
          </Card>
        </div>
      </div>

      {/* Key metrics row (moved above FastPass) */}
      <Row gutter={[24, 24]} style={{ marginTop: '16px' }}>
        <Col xs={24} md={12}>
          <Card className="metric-card" onClick={onActiveMembersClick} style={{ cursor: 'pointer' }} role="button" aria-label="View credit union breakdown">
            <div className="metric-content">
              <div className="metric-icon purple">
                <UserOutlined />
              </div>
              <div className="metric-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Title level={2} style={{ margin: 0 }}>{activeMembersCount}</Title>
                  <AntdTooltip title="Click to view credit union breakdown">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </AntdTooltip>
                </div>
                <Text type="secondary">Active Members</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Click to view credit union breakdown</Text>
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card className="metric-card">
            <div className="metric-content">
              <div className="metric-icon green">
                <DollarOutlined />
              </div>
              <div className="metric-info">
                <Title level={2} style={{ margin: 0 }}>${totalOpportunity}M</Title>
                <Text type="secondary">Total opportunity amount</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[24, 24]} className="main-content">
        {/* Left column: Pie Chart -> Top 5 -> Test Drive (stacked) */}
        <Col xs={24} lg={16}>
          <Card className="fastpass-card" style={{ height: 'auto' }}>
            <div className="fastpass-header">
              <div className="fastpass-title">
                <div className="title-icon">
                  <RiseOutlined />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Title level={3} style={{ margin: 0 }}>Total FastPass Hits</Title>
                    <AntdTooltip title="Breakdown of Right vs Left swipes. Click segments to drill in.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </AntdTooltip>
                  </div>
                  <Text type="secondary">Click segments to view details</Text>
                </div>
              </div>
              <div className="fastpass-stats">
                <Title level={1} style={{ color: '#1890ff', margin: 0 }}>
                  {rightSwipesData.length + leftSwipesData.length}
                </Title>
                <Text type="secondary">+12% vs last month</Text>
              </div>
            </div>

            <div className="chart-container">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                    onClick={handlePieClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <div className="chart-legend">
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: '#52c41a' }}></div>
                  <div>
                    <Text strong style={{ color: '#52c41a' }}>Right Swipes</Text>
                    <div style={{ color: '#666' }}>Interested customers</div>
                  </div>
                  <div className="legend-value">{rightSwipesData.length}</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: '#ff4d4f' }}></div>
                  <div>
                    <Text strong style={{ color: '#ff4d4f' }}>Left Swipes</Text>
                    <div style={{ color: '#666' }}>Not interested</div>
                  </div>
                  <div className="legend-value">{leftSwipesData.length}</div>
                </div>
              </div>
            </div>
          </Card>

          <Top5Component rightSwipesData={rightSwipesData} embedded={true} fixedHeight={220} />
          <TestDriveComponent rightSwipesData={rightSwipesData} embedded={true} fixedHeight={220} />
        </Col>

        {/* Right column: Hot Cars - card stretches to match left column height */}
        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <Card className="hotcars-card" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="fastpass-header">
              <div className="fastpass-title">
                <div className="title-icon">
                  <RiseOutlined />
                </div>
                <div>
                  <Title level={3} style={{ margin: 0 }}>Hot Cars</Title>
                  <Text type="secondary">Top 5 by right swipes</Text>
                </div>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <HotCarsComponent rightSwipesData={rightSwipesData} topN={5} embedded={true} compact={true} compactHeight={160} />
            </div>
          </Card>
        </Col>
      </Row>

      

      {/* bottom section removed to avoid duplication */}
    </div>
  );
};

export default OverviewSection;
